import { createSlice, isPending, PayloadAction } from '@reduxjs/toolkit';
import { storeResources } from '../resources';
import {
  ThunkGetTickets,
  ThunkGetTicketNotes,
  ThunkGetTicket,
  ThunkCreateTicketNote,
  ThunkGetTicketsFiltersOptions,
} from './thunks';
import { ITicketsInitialState } from './types';
import { ITicketNote } from '../../services/tickets/types';

const initialState: ITicketsInitialState = {
  tickets: [],
  notes: [],
  totalCount: 0,
  params: {
    page: 0,
    pageSize: 10,
    search: '',
    statusId: '',
    ticketStatusId: [],
    isClosed: null,
    _sort: 'id',
    _order: 1,
  },
  ticketsLoading: true,
  ticket: null,
  ticketsFiltersOptions: {
    openStatusId: '',
    closeStatusId: '',
    allStatuses: '',
    openStatuses: '',
    closedStatuses: '',
    useDefaultsStatuses: undefined,
  },
  pendingNotes: [],
};

const ticketsSlice = createSlice({
  name: storeResources.TICKETS,
  reducers: {
    addTicketChat: (state, action: { payload: { chatId: number; ticketId: string } }) => {
      state.tickets = state.tickets.map(ticket =>
        ticket.id === action.payload.ticketId
          ? { ...ticket, chatId: action.payload.chatId }
          : ticket,
      );

      state.ticket = state.ticket ? { ...state.ticket, chatId: action.payload.chatId } : null;
    },
    changeTicketSummary: (state, action: { payload: string }) => {
      state.params.search = action.payload;
      state.params.page = 0;
    },
    changeTicketsParamsPage: (state, action: PayloadAction<number>) => {
      state.params.page = action.payload;
    },
    changeTicketParamsPageSize: (state, action: PayloadAction<number>) => {
      state.params.pageSize = action.payload;
    },
    changeTicketFilterParams: (state, action: PayloadAction<{ statusId: string | null, ticketStatusId: number[] | string[] }>): void => {
      state.params = { ...state.params, ...action.payload, page: 0 };
    },
    changeCwTicketFilterParams: (state, action: PayloadAction<boolean | null>): void => {
      state.params.isClosed = action.payload;
      state.params.statusId = '';
      state.params.ticketStatusId = [];
      state.params.page = 0;
    },
    changeCustomTicketStatusFilter: (state, action: PayloadAction<number[]>): void => {
      state.params.ticketStatusId = action.payload;
      state.params.statusId = '';
      state.params.isClosed = null;
      state.params.page = 0;
    },
    sortById: state => {
      state.params._order = state.params._order === 1 ? 0 : 1;
    },
    clearTickets: state => {
      state.tickets = [];
      state.totalCount = 0;
      state.ticketsLoading = true;
    },
  },
  initialState,
  extraReducers: builder => {
    builder.addCase(ThunkGetTickets.fulfilled, (state, action) => {
      state.tickets = action.payload.tickets;
      state.totalCount = action.payload.total;

      state.params = action.meta.arg.params;

      state.ticketsLoading = false;
    });

    builder.addCase(ThunkGetTicketNotes.fulfilled, (state, action) => {
      state.notes = action.payload;
    });

    builder.addCase(ThunkGetTicket.fulfilled, (state, action) => {
      state.ticket = action.payload;
    });

    builder.addCase(ThunkCreateTicketNote.fulfilled, (state, action) => {
      const pendingNote = state.pendingNotes.find(i => i.requestId === action.meta.requestId);

      if (pendingNote) {
        state.notes = state.notes.map(msg =>
          msg.id === pendingNote.noteId ? { ...msg, status: undefined } : msg,
        );

        const pendingMessageIndex = state.pendingNotes.indexOf(pendingNote);

        state.pendingNotes.splice(1, pendingMessageIndex);
      }
    });

    builder.addCase(ThunkCreateTicketNote.pending, (state, action) => {
      const id = new Date().getTime();

      const note: ITicketNote = {
        createdBy: action.meta.arg.payload.contactName,
        dateCreated: new Date().toISOString(),
        text: action.meta.arg.payload.note,
        id,
        type: 'TicketNote',
        status: 'loading',
      };

      state.pendingNotes.push({ noteId: id, requestId: action.meta.requestId });
      state.notes.push(note);
    });

    builder.addCase(ThunkGetTicketsFiltersOptions.fulfilled, (state, action) => {
      state.ticketsFiltersOptions = action.payload;
    });

    builder.addMatcher(isPending(ThunkGetTickets), state => {
      state.ticketsLoading = true;
    });
  },
});

export const {
  changeTicketFilterParams,
  changeCwTicketFilterParams,
  changeCustomTicketStatusFilter,
  changeTicketSummary,
  changeTicketsParamsPage,
  changeTicketParamsPageSize,
  addTicketChat,
  sortById,
  clearTickets,
} = ticketsSlice.actions;
export default ticketsSlice.reducer;
